import { useRef, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { useAdMob } from '../../chat/hooks/useAdMob';
import useUserQuery from '../../../components/api/queries/useUserQuery';
import { useFeatureEnabled } from '@pocapan/shared/domains/features/ui/hooks/useFeatureEnabled';
import { useSuccessPhotoCardToast } from '../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { ReactComponent as LoadingIcon } from '../../../icons/streamline-ultimate_loading.svg';
import type { AdMobRewardData } from '../../../types/admob';
import { useCountdownToNextDay } from '../utils/useCountdownToNextDay';
import { useCountdownMinutes } from '../utils/useCountdownMinutes';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';

interface FreeEnergyChargeCardProps {
  item: {
    id: number;
    title: string;
    description: string;
    earned: string;
    type: 'attendance' | 'ads' | 'lounge' | 'findBuddy';
  };
  lastDailyCheckInRewardedAt?: string | null;
  lastAdRewardedAt?: string | null;
  lastPostRewardedAt?: string | null;
}

const FreeEnergyChargeCard = ({
  item,
  lastDailyCheckInRewardedAt,
  lastAdRewardedAt,
  lastPostRewardedAt
}: FreeEnergyChargeCardProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showSuccessToast = useSuccessPhotoCardToast();
  const earnedRewardRef = useRef<AdMobRewardData | null>(null);
  const user = useUserQuery();

  const { data: findBuddyFeature } = useFeatureEnabled(FEATURE_KEYS.FIND_BUDDY);
  const isFindBuddyEnabled = findBuddyFeature?.featureFlag?.enabled === true;

  // 출석체크: 다음날까지 카운트다운
  const attendanceCountdown = useCountdownToNextDay(
    item.type === 'attendance' ? lastDailyCheckInRewardedAt : null
  );

  // 광고: 5분 쿨타임 카운트다운
  const adsCountdown = useCountdownMinutes(item.type === 'ads' ? lastAdRewardedAt : null, 5);

  // 라운지: 24시간 지났는지 확인
  const isLoungeRewardedWithin24Hours = (() => {
    if (item.type !== 'lounge' || !lastPostRewardedAt) {
      return false;
    }
    const lastRewarded = new Date(lastPostRewardedAt);
    const now = new Date();
    const diff = now.getTime() - lastRewarded.getTime();
    const hours24 = 24 * 60 * 60 * 1000;
    return diff < hours24;
  })();

  // 카운트다운 텍스트 결정
  const getDisplayEarnedText = () => {
    if (item.type === 'attendance' && attendanceCountdown) {
      return t('BettieFreeCharge.freeChargeEnergy.countdown', {
        time: attendanceCountdown
      }) as string;
    }
    if (item.type === 'ads' && adsCountdown) {
      return t('BettieFreeCharge.freeChargeEnergy.countdown', {
        time: adsCountdown
      }) as string;
    }
    if (item.type === 'lounge' && isLoungeRewardedWithin24Hours) {
      return t('BettieFreeCharge.freeChargeEnergy.seeYouTomorrow') as string;
    }
    if (item.type === 'findBuddy' && isFindBuddyEnabled) {
      return t('BettieFreeCharge.freeChargeEnergy.energyCount', { count: 1 }) as string;
    }
    return item.earned;
  };

  // 비활성화 상태 결정
  const getIsDisabled = () => {
    if (item.type === 'attendance') {
      return lastDailyCheckInRewardedAt != null && attendanceCountdown != null;
    }
    if (item.type === 'ads') {
      return lastAdRewardedAt != null && adsCountdown != null;
    }
    if (item.type === 'lounge') {
      return isLoungeRewardedWithin24Hours;
    }
    if (item.type === 'findBuddy' && !isFindBuddyEnabled) {
      return true;
    }

    return false;
  };

  const displayEarnedText = getDisplayEarnedText();
  const isDisabled = getIsDisabled();
  const isFindBuddy = item.type === 'findBuddy';
  const isAdType = item.type === 'attendance' || item.type === 'ads';

  const showToastIfReady = useCallback(() => {
    if (earnedRewardRef.current) {
      const reward = earnedRewardRef.current;
      earnedRewardRef.current = null;
      showSuccessToast(
        'bottom',
        t('BettieFreeCharge.freeChargeEnergy.addEnergy', {
          type: reward.type,
          amount: reward.amount
        }) as string
      );
    }
  }, [showSuccessToast]);

  const { showAd, isLoading } = useAdMob({
    autoLoad: isAdType,
    adType: 'rewardedInterstitial',
    featureKind: item.type === 'attendance' || item.type === 'ads' ? item.type : undefined,
    onReward: (reward) => {
      earnedRewardRef.current = reward;
    },
    onAdClosed: () => {
      queryClient.invalidateQueries(['bettieEnergies']);
      showToastIfReady();
    }
  });

  const handleAdClick = () => {
    if (isLoading) {
      showSuccessToast(
        'bottom',
        t('BettieFreeCharge.freeChargeEnergy.loadAd') as string,
        false,
        <LoadingIcon width={20} height={20} />
      );
      return;
    }
    showAd();
  };

  const handleClick = () => {
    switch (item.type) {
      case 'attendance':
      case 'ads':
        handleAdClick();
        break;
      case 'lounge':
        navigate('/lounge-chat?categoryGroup=community&forReward=true');
        break;
      case 'findBuddy':
        if (isEmpty(user?.mbti)) {
          navigate('/fan-buddy/intro');
        } else {
          navigate('/fan-buddy/find');
        }
        break;
    }
  };

  const getBadgeStyles = () => {
    if (isFindBuddy && !isFindBuddyEnabled) {
      return {
        bg: 'bg-[#FAF5D2]',
        text: 'text-[#DEB316]',
        border: ''
      };
    }
    if (item.type === 'lounge' && isLoungeRewardedWithin24Hours) {
      return {
        bg: 'bg-pure-white',
        text: 'text-on-disable',
        border: 'border border-disabled-light'
      };
    }
    if (isDisabled) {
      return {
        bg: 'bg-disable',
        text: 'text-on-disable',
        border: ''
      };
    }
    return {
      bg: 'bg-warningContainer',
      text: 'bg-gradient-single-red bg-clip-text text-transparent',
      border: ''
    };
  };

  const badgeStyles = getBadgeStyles();

  return (
    <button
      type="button"
      className="flex items-center justify-between rounded-[12px] bg-background-elevated px-[18px] py-[16px] shadow-[0_0_4px_0_rgba(0,0,0,0.04),_0_0_24px_0_rgba(0,0,0,0.05)] dark:shadow-[0_0_4px_0_rgba(0,0,0,0.17),_0_0_24px_0_rgba(0,0,0,0.47)] disabled:cursor-not-allowed"
      onClick={handleClick}
      disabled={isDisabled}
    >
      <div className="flex flex-col items-start gap-[4px]">
        <p
          className={`text-[16px] font-semibold ${isFindBuddy && !isFindBuddyEnabled ? 'text-on-disable' : 'text-text-main-lv2'} leading-[19px]`}
        >
          {item.title}
        </p>
        <p className="text-[14px] text-text-sub-lv1 leading-[16px]">{item.description}</p>
      </div>
      <div className="relative">
        <div
          className={`px-[12px] py-[8px] rounded-[32px] ${badgeStyles.bg} ${badgeStyles.border}`}
        >
          <p className={`text-[14px] font-semibold leading-[17px] ${badgeStyles.text}`}>
            {displayEarnedText}
          </p>
        </div>
      </div>
    </button>
  );
};

export default FreeEnergyChargeCard;
